import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, Dimensions, ActivityIndicator, Modal, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRecipes } from '@/hooks/useRecipes';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { getContentWidth } from '@/constants/layout';
import { MAX_RECIPES_PER_USER } from '@/constants/limits';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals } from '@/constants/theme';
import { RecipeCard } from '@/components/recipes/RecipeCard';
import { RecipesEmptyState } from '@/components/recipes/RecipesEmptyState';

const CONTAINER_PADDING = 16;
const CARD_GAP = 16;
const MOBILE_BREAKPOINT = 600;
const TWO_COL_BREAKPOINT = 900;
const THREE_COL_BREAKPOINT = 1200;

function getColumnCount(contentWidth: number): number {
  if (contentWidth < MOBILE_BREAKPOINT) return 1;
  if (contentWidth < TWO_COL_BREAKPOINT) return 2;
  if (contentWidth < THREE_COL_BREAKPOINT) return 3;
  return 4;
}

export default function RecipeLibrary() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { recipes, loading, error, refetch } = useRecipes();
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalTitle, setAlertModalTitle] = useState('');
  const [alertModalMessage, setAlertModalMessage] = useState('');

  const showAlert = useCallback((title: string, message: string) => {
    setAlertModalTitle(title);
    setAlertModalMessage(message);
    setShowAlertModal(true);
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const contentWidth = containerWidth ?? getContentWidth(Dimensions.get('window').width);
  const columnCount = getColumnCount(contentWidth);
  const gridInnerWidth = contentWidth - CONTAINER_PADDING * 2;
  const cardWidth = columnCount === 1
    ? gridInnerWidth
    : (gridInnerWidth - CARD_GAP * (columnCount - 1)) / columnCount;

  const hasReachedRecipeLimit = recipes.length >= MAX_RECIPES_PER_USER;
  const allTags = Array.from(new Set(recipes.flatMap(r => r.tags)));

  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recipe.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recipe.ingredients.some(ing => ing.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesTags = selectedTags.length === 0 || 
      selectedTags.every(tag => recipe.tags.includes(tag));

    return matchesSearch && matchesTags;
  });

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const renderRecipe = (recipe: typeof recipes[0]) => (
    <RecipeCard
      testID={`recipe-card-${recipe.id}`}
      recipe={recipe}
      columnCount={columnCount}
      onPress={() => router.push(`/(tabs)/recipes/${recipe.id}`)}
    />
  );

  return (
    <View
      style={styles.container}
      testID="recipes-screen"
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
    >
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { width: '100%', flexGrow: 1, paddingTop: 16 + insets.top },
        ]}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <Ionicons name="sparkles" size={24} color={Brand.primary} />
            <Text style={styles.headerTitle}>My Recipes</Text>
          </View>
          <Text style={styles.headerSubtitle}>Discover delicious meals to cook today</Text>
          <TouchableOpacity
            onPress={() => router.push('/(tabs)/recipes/import')}
            style={styles.importLink}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="link" size={16} color={Brand.primary} />
            <Text style={styles.importLinkText}>Import from URL</Text>
          </TouchableOpacity>
        </View>

        {/* Search */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={20} color={Neutrals.textMuted} style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search for recipes or ingredients..."
              placeholderTextColor={Neutrals.textMuted}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>

          {/* Tags */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tagsScroll}>
            <View style={styles.filterRow}>
              <View style={styles.filterLabel}>
                <Ionicons name="filter" size={16} color={Neutrals.textSecondary} />
                <Text style={styles.filterText}>Filter:</Text>
              </View>
              {allTags.map(tag => (
                <TouchableOpacity
                  key={tag}
                  onPress={() => toggleTag(tag)}
                  style={[
                    styles.tagButton,
                    selectedTags.includes(tag) && styles.tagButtonActive,
                  ]}
                >
                  <Text
                    style={[
                      styles.tagButtonText,
                      selectedTags.includes(tag) && styles.tagButtonTextActive,
                    ]}
                  >
                    {tag}
                  </Text>
                </TouchableOpacity>
              ))}
              {selectedTags.length > 0 && (
                <TouchableOpacity
                  onPress={() => setSelectedTags([])}
                  style={styles.clearButton}
                >
                  <Text style={styles.clearButtonText}>Clear all</Text>
                </TouchableOpacity>
              )}
            </View>
          </ScrollView>
        </View>

        {/* Recipe Grid */}
        {loading ? (
          <View style={styles.emptyState} testID="recipes-empty-loading">
            <ActivityIndicator size="large" color={Brand.primary} />
            <Text style={styles.emptyText}>Loading recipes...</Text>
          </View>
        ) : error ? (
          <View style={styles.emptyState} testID="recipes-empty-error">
            <View style={styles.emptyIcon}>
              <Ionicons name="alert-circle" size={40} color={Brand.destructive} />
            </View>
            <Text style={styles.emptyText}>Failed to load recipes. Please try again.</Text>
          </View>
        ) : filteredRecipes.length === 0 ? (
          <RecipesEmptyState
            variant={
              recipes.length === 0
                ? hasReachedRecipeLimit
                  ? 'limit'
                  : 'no-recipes'
                : 'no-results'
            }
            onAddRecipe={
              recipes.length === 0 && !hasReachedRecipeLimit
                ? () => router.push('/(tabs)/recipes/new')
                : undefined
            }
            maxRecipes={MAX_RECIPES_PER_USER}
          />
        ) : (
          <View style={[styles.recipesGrid, styles.recipesGridResponsive, { gap: CARD_GAP }]} testID="recipe-list">
            {filteredRecipes.map((recipe) => (
              <View key={recipe.id} style={[styles.recipeCardWrapper, { width: cardWidth }]}>
                {renderRecipe(recipe)}
              </View>
            ))}
          </View>
        )}
      </ScrollView>
      
      {/* Floating Action Button */}
      <TouchableOpacity
        onPress={() => {
          if (hasReachedRecipeLimit) {
            showAlert('Limit reached', `Maximum of ${MAX_RECIPES_PER_USER} recipes. Delete a recipe to add more.`);
            return;
          }
          router.push('/(tabs)/recipes/new');
        }}
        style={[styles.fab, hasReachedRecipeLimit && styles.fabDisabled]}
        activeOpacity={0.8}
      >
        <LinearGradient
          colors={[...Brand.primaryGradientStrong]}
          style={styles.fabGradient}
        >
          <Ionicons name="add" size={28} color="#fff" />
        </LinearGradient>
      </TouchableOpacity>

      <Modal
        visible={showAlertModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAlertModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{alertModalTitle}</Text>
            <Text style={styles.modalDescription}>{alertModalMessage}</Text>
            <View style={styles.modalActions}>
              <Button onPress={() => setShowAlertModal(false)} style={styles.modalButton}>
                OK
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 0,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollView: {
    flex: 1,
    minWidth: 0,
  },
  content: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  header: {
    marginBottom: 24,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Brand.primary,
  },
  headerSubtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
  },
  importLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  importLinkText: {
    fontSize: 15,
    color: Brand.primary,
    fontWeight: '500',
  },
  searchContainer: {
    marginBottom: 24,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Neutrals.whiteFaded70,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: Neutrals.border,
    marginBottom: 12,
    paddingHorizontal: 16,
    height: 56,
    elevation: Platform.OS === 'android' ? 0 : undefined,
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: Neutrals.textPrimary,
  },
  tagsScroll: {
    marginHorizontal: -16,
    paddingHorizontal: 16,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  filterLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Neutrals.whiteFaded70,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 999,
    elevation: Platform.OS === 'android' ? 0 : undefined,
  },
  filterText: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textSecondary,
  },
  tagButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: Neutrals.whiteFaded70,
    elevation: Platform.OS === 'android' ? 0 : undefined,
  },
  tagButtonActive: {
    backgroundColor: Brand.primary,
  },
  tagButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  tagButtonTextActive: {
    color: '#fff',
  },
  clearButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  clearButtonText: {
    fontSize: 12,
    color: Brand.primary,
  },
  recipesGrid: {
    paddingBottom: 8,
  },
  recipesGridResponsive: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  recipeCardWrapper: {
    flexShrink: 0,
  },
  recipeCard: {
    backgroundColor: Neutrals.whiteFaded70,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: Neutrals.whiteFaded70,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 2,
  },
  imageContainer: {
    width: '100%',
    aspectRatio: 4 / 3,
    backgroundColor: Neutrals.cardBgSolid,
    overflow: 'hidden',
  },
  recipeImage: {
    width: '100%',
    height: '100%',
  },
  recipeImagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: Neutrals.cardBgSolid,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
  },
  recipeStats: {
    position: 'absolute',
    bottom: 12,
    left: 12,
    right: 12,
    flexDirection: 'row',
    gap: 8,
  },
  statBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Neutrals.whiteFaded90,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
  },
  statText: {
    fontSize: 12,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  recipeHeader: {
    padding: 20,
    paddingBottom: 12,
  },
  recipeHeaderNoImage: {
    paddingTop: 20,
  },
  recipeStatsInline: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  recipeTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
    lineHeight: 24,
  },
  recipeDescription: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    lineHeight: 20,
  },
  recipeTags: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    marginRight: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 24,
    backgroundColor: Neutrals.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Brand.primary,
    borderRadius: 12,
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  emptyButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 20,
  },
  limitText: {
    color: Neutrals.textSecondary,
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
  },
  emptyText: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    textAlign: 'center',
  },
  fab: {
    position: 'absolute',
    bottom: 100,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 16,
    shadowColor: Brand.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 8,
  },
  fabDisabled: {
    opacity: 0.5,
  },
  fabGradient: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
  },
});
